import { useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { ICardTransactionFilterFormFields } from '@/store/types/card-transactions.type';
import { parseAllParameters, periodBases, rangeBases } from '@/ui-kit/table-filters/filters.type';
import {
  mapCardTransactionSearch,
  useCardTransactionSearchField,
} from '@/features/cards/hooks/CardTransactions/useCardTransactionSearchField';
import { TCardTransactionFormFilters } from '@/features/cards/store/models/card.model';
import { useHasFilter } from '@/hooks/useHasFilter';
import {
  mapCardTransactionStatus,
  useCardTransactionStatusField,
} from '@/features/cards/hooks/CardTransactions/useCardTransactionStatusField';
import {
  mapCardTransactionDate,
  useCardTransactionDateField,
} from '@/features/cards/hooks/CardTransactions/useCardTransactionDateField';
import {
  mapCardTransactionAmount,
  useCardTransactionAmountField,
} from '@/features/cards/hooks/CardTransactions/useCardTransactionAmountField';
import {
  mapCardTransactionMcc,
  useCardTransactionMccField,
} from '@/features/cards/hooks/CardTransactions/useCardTransactionMccField';
import {
  mapCardTransactionMid,
  useCardTransactionMidField,
} from '@/features/cards/hooks/CardTransactions/useCardTransactionMidField';
import {
  mapCardTransactionPaymentTypes,
  useCardTransactionPaymentTypeField,
} from '@/features/cards/hooks/CardTransactions/useCardTransactionPaymentTypeField';

export const parseCardTransactionFiltersValue = (
  type: keyof ICardTransactionFilterFormFields,
  value,
): TCardTransactionFormFilters => {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (type === 'search') {
    return mapCardTransactionSearch(value);
  } else if (type === 'createdDate') {
    return mapCardTransactionDate(value);
  } else if (type === 'amount') {
    return mapCardTransactionAmount(value);
  } else if (type === 'status') {
    return mapCardTransactionStatus(value);
  } else if (type === 'mcc') {
    return mapCardTransactionMcc(value);
  } else if (type === 'mid') {
    return mapCardTransactionMid(value);
  } else if (type === 'paymentTypes') {
    return mapCardTransactionPaymentTypes(value);
  }
};

export function useCardTransactionFilters({ onChange }) {
  const defaultValues: ICardTransactionFilterFormFields = {
    amount: { from: null, to: null, base: rangeBases.EQUAL },
    createdDate: { from: null, to: null, base: periodBases.BETWEEN },
  };

  const methods = useForm<ICardTransactionFilterFormFields>({ defaultValues });

  const getQueryParameters = useCallback(() => {
    const values = methods.getValues();
    return parseAllParameters<ICardTransactionFilterFormFields, TCardTransactionFormFilters>(
      values,
      parseCardTransactionFiltersValue,
    );
  }, [methods]);

  const handleClear = useCallback(() => {
    methods.reset();
    onChange(null);
  }, [methods, onChange]);

  const handleChange = useCallback(() => {
    onChange(getQueryParameters());
  }, [getQueryParameters, onChange]);

  const search = useCardTransactionSearchField(methods, handleChange);
  const createdDate = useCardTransactionDateField(methods, handleChange);
  const amount = useCardTransactionAmountField(methods, handleChange);
  const status = useCardTransactionStatusField(methods, handleChange);
  const mcc = useCardTransactionMccField(methods, handleChange);
  const mid = useCardTransactionMidField(methods, handleChange);
  const paymentTypes = useCardTransactionPaymentTypeField(methods, handleChange);

  const hasFilters = useHasFilter({ search, createdDate, amount, status, mcc, mid, paymentTypes });

  return { clearFilters: handleClear, search, createdDate, amount, status, mcc, mid, paymentTypes, hasFilters };
}
